import { getSavedHolders } from 'api/checkoutpage';
import type { SavedHolder, SavedHolderMe } from 'api/checkoutpage/savedHolders';
import type { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';
import { useIsAuthenticated } from 'react-auth-kit';
import { useQuery, useQueryClient } from 'react-query';
import { normalizeIraqiPhone } from 'utils/iraqiPhone';
import { fullNameSchema } from 'utils/validation';

export const SAVED_HOLDERS_QUERY_KEY = 'savedHolders';

/** Stable identity, so a consumer with `[holders]` in a dependency array does not re-run forever. */
const NO_HOLDERS: SavedHolder[] = [];

/**
 * Whether a holder can actually fill a holder card, judged by the **same rules the form uses**:
 * `fullNameSchema` for the name, and `normalizeIraqiPhone` for the phone (which guarantees the
 * national form satisfies `iraqiPhoneNumberSchema`).
 *
 * The sheet must not offer a holder the form would then reject. TZ-CO-16 means picking one is not
 * a dead-end — the card simply refuses to collapse and shows the error — but an entry the buyer
 * cannot use without editing does not belong in a list of one-tap shortcuts.
 *
 * Module scope so `select` keeps a stable identity across renders.
 */
const isUsableInHolderForm = ({ name, phone }: SavedHolder) =>
  fullNameSchema.isValidSync(name) && normalizeIraqiPhone(phone).ok;

/**
 * Only `holders` is filtered. **`me` deliberately is not**: it is the buyer themselves, its `name`
 * is nullable by contract, and a phone-only `me` still usefully prefills ticket 1 — filtering it
 * out would discard the buyer's own number over a missing name.
 */
const selectSavedHolders = ({ me, holders }: FetchedSavedHolders) => ({
  me,
  holders: holders.filter(isUsableInHolderForm),
});

type FetchedSavedHolders = Awaited<ReturnType<typeof getSavedHolders>>;

type UseSavedHoldersOptions = {
  /** Escape hatch for tests; production always fetches on mount. */
  enabled?: boolean;
  /** Called when a request made on a live session comes back `401` — an expired/revoked token. */
  onAuthRequired?: () => void;
};

type UseSavedHoldersResult = {
  /** The buyer's own contact, for auto-assigning ticket 1. `undefined` until it arrives. */
  me?: SavedHolderMe;
  holders: SavedHolder[];
  isLoading: boolean;
  /** True when the fetch failed. The sheet renders its empty state either way. */
  isError: boolean;
};

/**
 * Reads the buyer's own contact plus their saved ticket holders.
 *
 * **Fetched once, when the checkout page loads.** `me` auto-assigns ticket 1 straight away, and the
 * same cached response feeds the saved-holders sheet for every other ticket — so opening the sheet
 * is a cache hit, never a request.
 *
 * *(This supersedes TZ-CO-4/TZ-CO-5's lazy `enabled`-on-first-open design: the flow now needs the
 * data before the buyer touches anything, because ticket 1 is filled for them.)*
 *
 * The query uses a single shared key with `staleTime: Infinity`, so the holders — which do not
 * change while the buyer is paying — are fetched exactly once per checkout session.
 *
 * **Never blocks the holder step.** Any failure (5xx, timeout, malformed body) resolves to an
 * empty list rather than an error surface; manual entry is always available, so the purchase is
 * never blocked. A `401` additionally invokes `onAuthRequired` so the existing login modal can
 * open — note the axios interceptor already attempts a token refresh before that point.
 *
 * **Nothing is requested while signed out**, so a signed-out buyer who opens the sheet simply sees
 * its empty state rather than an unprompted login modal.
 */
export const useSavedHolders = ({
  enabled = true,
  onAuthRequired,
}: UseSavedHoldersOptions = {}): UseSavedHoldersResult => {
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();
  const isSignedIn = isAuthenticated();

  // Saved holders are another person's name and phone number once the buyer signs out, so the
  // cached list is dropped the moment the session ends rather than lingering for whoever signs in
  // next. Keying the query per buyer would be the alternative, but the auth layer exposes no
  // stable user identifier and the access token rotates on refresh.
  //
  // Only on the signed-in → signed-out **transition**: dropping it whenever the buyer merely *is*
  // signed out would have every mounted holder card evict the shared query, and each eviction
  // makes the others refetch — one request per card instead of one per session.
  const wasSignedIn = useRef(isSignedIn);
  useEffect(() => {
    if (wasSignedIn.current && !isSignedIn) {
      queryClient.removeQueries(SAVED_HOLDERS_QUERY_KEY);
    }
    wasSignedIn.current = isSignedIn;
  }, [isSignedIn, queryClient]);

  const { data, isFetching, isError } = useQuery(SAVED_HOLDERS_QUERY_KEY, getSavedHolders, {
    // Also gated on the session, not just on the caller's flag. Saved holders belong to one buyer
    // and the endpoint requires auth, so a request made while signed out is known-useless — and
    // worse, it would undo the eviction below: dropping the cache while an enabled observer is
    // mounted makes react-query immediately refetch and repopulate it (measured: 1 → 2 requests,
    // with the previous buyer's holders back in the cache).
    enabled: enabled && isSignedIn,
    staleTime: Infinity,
    select: selectSavedHolders,
    onError: (error) => {
      // The request only fires while the client believes it is signed in, so a 401 means the
      // server disagrees — an expired or revoked session. That is exactly the re-auth case.
      const status = (error as AxiosError)?.response?.status;
      if (status === 401) {
        onAuthRequired?.();
      }
    },
  });

  // Signed out means nothing, full stop — not "whatever this observer last rendered". Evicting the
  // cache does not reset a mounted observer's result, so without this the sheet would keep painting
  // the previous buyer's names and numbers after they signed out.
  return {
    me: isSignedIn ? data?.me : undefined,
    holders: (isSignedIn ? data?.holders : undefined) ?? NO_HOLDERS,
    isLoading: isFetching,
    isError,
  };
};
